import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchCiternes } from '@/store/thunks/citerneThunks';
import { createTransfer, updateTransfer } from '@/store/thunks/movementThunks';
import type { MovementRecord } from '@/store/slices/movementSlice';
import type { Citerne } from '@/types';
import { Input, Button, SearchSelect, Textarea } from '@/components/ui';

const schema = Yup.object({
  citerneEmetteurId: Yup.string().required('Citerne émetteur obligatoire'),
  citerneDestinataireId: Yup.string().required('Citerne destinataire obligatoire'),
  date: Yup.string().required('Date obligatoire'),
  quantite: Yup.number().required('Quantité obligatoire').min(0),
  remarque: Yup.string(),
});

interface TransferFormProps {
  transfer?: MovementRecord | null;
  type: 'create' | 'update';
  onClose: () => void;
  onSuccess?: () => void;
}

export default function TransferForm({ transfer, type, onClose, onSuccess }: TransferFormProps) {
  const dispatch = useAppDispatch();
  const citernes = useAppSelector((s: RootState) => s.citerne.citernes);

  useEffect(() => {
    dispatch(fetchCiternes());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      citerneEmetteurId: transfer?.citerne_emetteur ? String((transfer.citerne_emetteur as { id?: number }).id) : '',
      citerneDestinataireId: transfer?.citerne_destinataire ? String((transfer.citerne_destinataire as { id?: number }).id) : '',
      date: transfer?.date ? (transfer.date as string).toString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      quantite: transfer?.quantite ?? '',
      remarque: (transfer?.remarque as string) ?? '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const emetteur = citernes.find((c: Citerne) => String(c.id) === values.citerneEmetteurId);
      const destinataire = citernes.find((c: Citerne) => String(c.id) === values.citerneDestinataireId);
      if (!emetteur || !destinataire) return;
      const payload: MovementRecord = {
        citerne_emetteur: { id: emetteur.id, code: emetteur.code },
        citerne_destinataire: { id: destinataire.id, code: destinataire.code },
        date: values.date,
        quantite: Number(values.quantite),
        remarque: values.remarque || undefined,
      };
      if (type === 'update' && transfer?.id) {
        const res = await dispatch(updateTransfer({ ...payload, id: transfer.id }));
        if (updateTransfer.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      } else {
        const res = await dispatch(createTransfer(payload));
        if (createTransfer.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      }
    },
  });

  const citerneOptions = citernes.map((c: Citerne) => ({ value: String(c.id), label: c.code ?? String(c.id) }));

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
      <div className="flex gap-4">
        <SearchSelect
          label="Citerne émetteur"
          options={citerneOptions}
          value={formik.values.citerneEmetteurId}
          onChange={(v) => formik.setFieldValue('citerneEmetteurId', v)}
          onBlur={() => formik.setFieldTouched('citerneEmetteurId', true)}
          error={formik.touched.citerneEmetteurId && formik.errors.citerneEmetteurId ? String(formik.errors.citerneEmetteurId) : undefined}
          searchPlaceholder="Rechercher une citerne…"
        />
        <SearchSelect
          label="Citerne destinataire"
          options={citerneOptions}
          value={formik.values.citerneDestinataireId}
          onChange={(v) => formik.setFieldValue('citerneDestinataireId', v)}
          onBlur={() => formik.setFieldTouched('citerneDestinataireId', true)}
          error={formik.touched.citerneDestinataireId && formik.errors.citerneDestinataireId ? String(formik.errors.citerneDestinataireId) : undefined}
          searchPlaceholder="Rechercher une citerne…"
        />
      </div>
      <div className="flex gap-4">
        <Input label="Date" type="date" id="date" {...formik.getFieldProps('date')} error={formik.touched.date && formik.errors.date ? String(formik.errors.date) : undefined} />
        <Input label="Quantité" type="number" id="quantite" {...formik.getFieldProps('quantite')} error={formik.touched.quantite && formik.errors.quantite ? String(formik.errors.quantite) : undefined} />
      </div>
      <Textarea label="Remarque" id="remarque" rows={3} {...formik.getFieldProps('remarque')} />
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" loading={formik.isSubmitting}>{type === 'update' ? 'Modifier' : 'Créer'}</Button>
      </div>
    </form>
  );
}
